import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiHeart, FiShoppingCart } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useWishlist } from '../../context/WishlistContext';
import StarRating from '../common/StarRating';
import './ProductCard.css';

const picsum = (seed) => `https://picsum.photos/seed/${seed}/400/300`;

export default function ProductCard({ product }) {
  const { addItem, isInCart } = useCart();
  const { user } = useAuth();
  const { toggle, isWished } = useWishlist();

  const wished = isWished(product._id);
  const [hovered, setHovered] = useState(false);
  const [imgSrc, setImgSrc] = useState(
    product.images?.[0] || picsum(product._id || product.name)
  );

  const { _id, name, price, salePrice, rating, totalReviews, vendor, stock } = product;
  const activePrice = salePrice || price;
  const discount = salePrice ? Math.round((1 - salePrice / price) * 100) : 0;
  const inCart = isInCart(_id);
  const isDemo = !/^[a-f\d]{24}$/i.test(_id);

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDemo) {
      toast('Demo product — not available for purchase', { icon: '🛍️' });
      return;
    }
    addItem(product, 1);
    toast.success(
      <span><strong>{name.length > 22 ? name.slice(0, 22) + '…' : name}</strong> added to cart</span>
    );
  };

  const handleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return toast.error('Please login to use wishlist');
    toggle(product);
    toast.success(wished ? 'Removed from wishlist' : 'Added to wishlist');
  };

  return (
    <Link
      to={`/products/${_id}`}
      className="product-card"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Image */}
      <div className="pc-image-wrap">
        <img
          src={imgSrc}
          alt={name}
          className="pc-image"
          onError={() => setImgSrc(picsum(name))}
          loading="lazy"
        />

        {discount > 0 && <span className="pc-badge-discount">-{discount}%</span>}
        {stock === 0 && <span className="pc-badge-oos">Out of Stock</span>}

        {/* Wishlist */}
        <motion.button
          className={`pc-wishlist ${wished ? 'wished' : ''}`}
          onClick={handleWishlist}
          aria-label="Wishlist"
          whileTap={{ scale: 0.85 }}
        >
          <FiHeart size={15} />
        </motion.button>

        {/* Add to Cart slide-up */}
        <AnimatePresence>
          {hovered && (
            <motion.button
              className={`pc-add-btn ${inCart ? 'in-cart' : ''}`}
              onClick={handleAddToCart}
              disabled={stock === 0}
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              whileTap={{ scale: 0.98 }}
            >
              <FiShoppingCart size={14} />
              {inCart ? 'In Cart' : stock === 0 ? 'Out of Stock' : 'Add to Cart'}
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* Info */}
      <div className="pc-info">
        {vendor?.storeName && (
          <span className="pc-vendor">{vendor.storeName}</span>
        )}
        <h3 className="pc-name">{name}</h3>
        <StarRating rating={rating} totalReviews={totalReviews} size={12} />
        <div className="pc-prices">
          <span className="pc-price">Rs. {activePrice.toFixed(0)}</span>
          {salePrice && (
            <span className="pc-price-orig">Rs. {price.toFixed(0)}</span>
          )}
        </div>
      </div>
    </Link>
  );
}
